import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Lock, Unlock, Package, Check, X, AlertTriangle,
  Loader2, Save, Key, Users, Hash, DollarSign, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCompany } from '../context/CompanyContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  core: 'Core Modules',
  optional: 'Optional Modules',
  addon: 'Add-Ons',
  premium: 'Premium / External',
};

const CATEGORY_COLORS = {
  core: 'border-sky-200 bg-sky-50',
  optional: 'border-slate-200 bg-white',
  addon: 'border-amber-200 bg-amber-50',
  premium: 'border-purple-200 bg-purple-50',
};

// ==================== PASSWORD GATE ====================
const PasswordGate = ({ onVerified }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/licensing/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        onVerified();
      } else {
        setError('Invalid admin password');
      }
    } catch {
      setError('Connection error');
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="license-password-gate">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <Lock className="w-8 h-8 text-slate-500" />
          </div>
          <CardTitle className="text-lg">License Administration</CardTitle>
          <p className="text-sm text-slate-500 mt-1">This area is password protected</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <Input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter admin password"
                autoFocus
                data-testid="license-password-input"
              />
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>
            <Button type="submit" className="w-full" disabled={loading || !password} data-testid="license-verify-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Access Licensing
            </Button>
            <p className="text-xs text-slate-400 text-center">Default: BlueSky2026!</p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== MODULE CARD ====================
const ModuleCard = ({ mod, isActive, onToggle, disabled }) => {
  const isRequired = mod.requires_install;

  return (
    <div
      className={`p-4 rounded-lg border transition-all ${
        isActive
          ? 'border-sky-400 bg-sky-50 ring-1 ring-sky-300'
          : 'border-slate-200 bg-white hover:border-slate-300'
      } ${disabled ? 'opacity-50' : ''}`}
      data-testid={`module-card-${mod.code}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{mod.code}</span>
            <h4 className="font-medium text-slate-800 truncate">{mod.name}</h4>
          </div>
          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{mod.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
            <span>${mod.price?.toLocaleString()}</span>
            <span className="text-slate-300">|</span>
            <span>${mod.maintenance_fee}/yr maintenance</span>
            {mod.requires?.length > 0 && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-amber-500">Requires: {mod.requires.join(', ')}</span>
              </>
            )}
            {isRequired && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-purple-500">Requires installation</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => onToggle(mod.code, !isActive)}
          disabled={disabled}
          className={`ml-3 flex-shrink-0 w-12 h-6 rounded-full transition-colors relative ${
            isActive ? 'bg-sky-500' : 'bg-slate-300'
          }`}
          data-testid={`toggle-${mod.code}`}
        >
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            isActive ? 'translate-x-6' : 'translate-x-0.5'
          }`} />
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
const LicensingPage = () => {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.company_id || 'default';

  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registry, setRegistry] = useState({});
  const [license, setLicense] = useState(null);
  const [payrollSubs, setPayrollSubs] = useState({});

  // License form state
  const [serialNumber, setSerialNumber] = useState('');
  const [clientStart, setClientStart] = useState(1);
  const [clientEnd, setClientEnd] = useState(5);
  const [additionalUsers, setAdditionalUsers] = useState(0);
  const [additionalClients, setAdditionalClients] = useState(0);
  const [activePayrollSubs, setActivePayrollSubs] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [regRes, licRes] = await Promise.all([
        fetch(`${API_URL}/api/licensing/modules`),
        fetch(`${API_URL}/api/licensing/license?company_id=${companyId}`),
      ]);
      if (regRes.ok) {
        const data = await regRes.json();
        setRegistry(data.modules || {});
        setPayrollSubs(data.payroll_sub_modules || {});
      }
      if (licRes.ok) {
        const data = await licRes.json();
        setLicense(data);
        setSerialNumber(data.serial_number || '');
        setClientStart(data.client_range_start || 1);
        setClientEnd(data.client_range_end || 5);
        setAdditionalUsers(data.additional_users || 0);
        setAdditionalClients(data.additional_client_packs || 0);
        setActivePayrollSubs(data.payroll_sub_modules || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { if (verified) fetchData(); }, [verified, fetchData]);

  const handleToggle = async (code, enable) => {
    try {
      const res = await fetch(`${API_URL}/api/licensing/license/toggle-module?company_id=${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module_code: code, enabled: enable }),
      });
      if (res.ok) {
        toast.success(`${code} ${enable ? 'enabled' : 'disabled'}`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Error'); }
  };

  const handleApplyBundle = async (bundleId) => {
    try {
      const res = await fetch(`${API_URL}/api/licensing/license/apply-bundle?company_id=${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bundle_id: bundleId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Error'); }
  };

  const handleSaveLicense = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/licensing/license?company_id=${companyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serial_number: serialNumber || null,
          client_range_start: clientStart,
          client_range_end: clientEnd,
          active_modules: license?.active_modules || [],
          payroll_sub_modules: activePayrollSubs,
          additional_users: additionalUsers,
          additional_client_packs: additionalClients,
        }),
      });
      if (res.ok) {
        toast.success('License saved');
        fetchData();
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const togglePayrollSub = (subCode) => {
    setActivePayrollSubs(prev =>
      prev.includes(subCode)
        ? prev.filter(s => s !== subCode)
        : [...prev, subCode]
    );
  };

  // Show password gate
  if (!verified) return <PasswordGate onVerified={() => setVerified(true)} />;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  const activeModules = license?.active_modules || [];
  const pricing = license?.pricing_summary || {};

  // Group modules by category
  const grouped = {};
  Object.values(registry).forEach(mod => {
    const cat = mod.category || 'optional';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(mod);
  });

  return (
    <div className="space-y-6" data-testid="licensing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">License Management</h1>
          <p className="text-slate-500">Blue Skies Digital module activation and licensing</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            license?.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
          }`} data-testid="license-status">
            {license?.status === 'active' ? 'Licensed' : 'Unlicensed'}
          </span>
          <Button onClick={handleSaveLicense} disabled={saving} data-testid="save-license-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save License
          </Button>
        </div>
      </div>

      {/* License Info + Pricing Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Serial Number</label>
              <Input value={serialNumber} onChange={e => setSerialNumber(e.target.value)} placeholder="Auto-generated" data-testid="serial-number-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-500">Client Range Start</label>
                <Input type="number" value={clientStart} onChange={e => setClientStart(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Client Range End</label>
                <Input type="number" value={clientEnd} onChange={e => setClientEnd(Number(e.target.value))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-500">Additional Users</label>
                <Input type="number" min={0} value={additionalUsers} onChange={e => setAdditionalUsers(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Additional Client Packs (5 each)</label>
                <Input type="number" min={0} value={additionalClients} onChange={e => setAdditionalClients(Number(e.target.value))} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center space-y-2">
            <p className="text-xs text-slate-500">License Price</p>
            <p className="text-3xl font-bold text-slate-800">${(pricing.total_license_price || 0).toLocaleString()}</p>
            <p className="text-xs text-slate-500">Annual Maintenance</p>
            <p className="text-2xl font-bold text-sky-600">${(pricing.total_annual_maintenance || 0).toLocaleString()}/yr</p>
            <p className="text-xs text-slate-400">{pricing.module_count || 0} modules active</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-2">
            <p className="text-xs font-medium text-slate-500 mb-2">Quick Bundles</p>
            <button
              onClick={() => handleApplyBundle('basic')}
              className="w-full p-3 rounded-lg border border-sky-200 bg-sky-50 hover:bg-sky-100 text-left transition-colors"
              data-testid="apply-basic-bundle"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sky-800 text-sm">Basic System</p>
                  <p className="text-xs text-sky-600">GL + CA2 + Payroll + Check Reconciliation</p>
                </div>
                <span className="font-bold text-sky-700">$3,875</span>
              </div>
            </button>
            <button
              onClick={() => handleApplyBundle('flc')}
              className="w-full p-3 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 text-left transition-colors"
              data-testid="apply-flc-bundle"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-purple-800 text-sm">FLC Bundle</p>
                  <p className="text-xs text-purple-600">Farm Labor Contractor + CA2 + Mag Media</p>
                </div>
                <span className="font-bold text-purple-700">$2,690</span>
              </div>
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Module List by Category */}
      {Object.entries(grouped).map(([cat, modules]) => (
        <div key={cat}>
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">
            {CATEGORY_LABELS[cat] || cat}
          </h3>
          <div className="space-y-2">
            {modules.map(mod => (
              <ModuleCard
                key={mod.code}
                mod={mod}
                isActive={activeModules.includes(mod.code)}
                onToggle={handleToggle}
                disabled={false}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Payroll Sub-Modules (shown when PR is active) */}
      {activeModules.includes('PR') && (
        <div>
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">
            Payroll Sub-Modules
          </h3>
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(payrollSubs).map(([code, sub]) => {
                  const isActive = activePayrollSubs.includes(code);
                  return (
                    <label
                      key={code}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                        isActive ? 'border-sky-300 bg-sky-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`payroll-sub-${code}`}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => togglePayrollSub(code)}
                        className="rounded"
                      />
                      <div>
                        <p className="text-sm font-medium">{sub.name}</p>
                        <p className="text-xs text-slate-400">{sub.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Modules Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active Module Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {license?.module_details?.length > 0 ? (
            <table className="w-full text-sm" data-testid="module-summary-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-2 font-medium text-slate-600">Code</th>
                  <th className="p-2 font-medium text-slate-600">Module</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Qty</th>
                  <th className="p-2 font-medium text-slate-600 text-right">License Price</th>
                  <th className="p-2 font-medium text-slate-600 text-right">Annual Maintenance</th>
                </tr>
              </thead>
              <tbody>
                {license.module_details.map(m => (
                  <tr key={m.code} className="border-b">
                    <td className="p-2 font-mono">{m.code}</td>
                    <td className="p-2">{m.name}</td>
                    <td className="p-2 text-right">{m.quantity}</td>
                    <td className="p-2 text-right">${m.total_price?.toLocaleString()}</td>
                    <td className="p-2 text-right">${m.total_maintenance?.toLocaleString()}/yr</td>
                  </tr>
                ))}
                <tr className="font-bold bg-slate-50">
                  <td className="p-2" colSpan={3}>Total</td>
                  <td className="p-2 text-right">${(pricing.total_license_price || 0).toLocaleString()}</td>
                  <td className="p-2 text-right">${(pricing.total_annual_maintenance || 0).toLocaleString()}/yr</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-slate-400 text-center py-6">No modules activated. Select modules above or apply a bundle.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LicensingPage;
